import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { BillingService } from 'app/services/billing/billing.service';
import { BillingData, BillingResponse } from 'app/services/billing/billing.type';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { UpdatePlanModalComponent } from './modals/update-plan-modal.component';

@Component({
    selector: 'plan-subscription',
    templateUrl: './plan-subscription.component.html',
    styleUrls: ['./plan-subscription.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule
    ]
})
export class PlanSubscriptionComponent implements OnInit {
    billingData: BillingData | null = null;
    isLoading: boolean = true;

    constructor(
        private _billingService: BillingService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _alertService: AlertService,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadSubscriptionData();
    }

    loadSubscriptionData(): void {
        this.isLoading = true;

        this._billingService.getSubscription()
            .pipe(
                finalize(() => {
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (response: BillingResponse) => {
                    this.billingData = response.data;
                },
                error: (response) => {
                    this._alertService.error(response?.error, {autoClose: true});
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
    }

    cancelSubscription(): void {
        // Esta função será implementada no futuro
        console.log('Cancelar assinatura');
    }

    openUpdatePlanModal(): void {
        if (!this.billingData?.plan) {
            this._alertService.error('Dados da assinatura não disponíveis', { autoClose: true });
            return;
        }

        const dialogRef = this._dialog.open(UpdatePlanModalComponent, {
            width: '600px',
            maxWidth: '100%',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            panelClass: 'update-plan-modal-panel',
            data: {
                currentPlan: {
                    id: this.billingData.plan.id,
                    name: this.billingData.plan.name,
                    stripePriceId: this.billingData.plan.stripePriceId
                },
                memberCount: this.billingData.church?.memberCount || 0
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.action === 'updated') {
                // Recarrega os dados da assinatura
                this.loadSubscriptionData();
            }
        });
    }

    getPlanDisplayName(planName: string): string {
        const planNames: { [key: string]: string } = {
            'bronze': 'Bronze',
            'silver': 'Prata',
            'gold': 'Ouro',
            'diamond': 'Diamond'
        };
        return planNames[planName.toLowerCase()] || planName;
    }

    getPlanMedalColor(planName: string): string {
        const colors: { [key: string]: string } = {
            'bronze': 'text-orange-600',
            'silver': 'text-gray-400',
            'gold': 'text-yellow-500',
            'diamond': 'text-cyan-600'
        };
        return colors[planName.toLowerCase()] || 'text-gray-600';
    }

    getFormattedFeatures(): string[] {
        if (!this.billingData?.plan?.features) {
            return [];
        }

        const features = this.billingData.plan.features;

        // Se features já é um array de strings, retorna diretamente
        if (Array.isArray(features)) {
            return features;
        }

        // Caso contrário, formata o objeto PlanFeatures
        const formattedFeatures: string[] = [];

        if (features.storage_gb) {
            formattedFeatures.push(`${features.storage_gb} GB de armazenamento`);
        }

        if (features.members_limit) {
            formattedFeatures.push(`Até ${features.members_limit} membros`);
        }

        if (features.basic_reports) {
            formattedFeatures.push('Relatórios básicos');
        }

        if (features.advanced_reports) {
            formattedFeatures.push('Relatórios avançados');
        }

        if (features.priority_support) {
            formattedFeatures.push('Suporte prioritário');
        }

        return formattedFeatures;
    }
}
